import _ from 'lodash';
import React, {useContext, useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import Select from 'react-select';
import {
    BrokerReportAssignmentLine,
    BrokerReportDispatchOrderSection,
    BrokerReportForemanGroup,
    BrokerReportGroup,
    BrokerReportJobGroup,
    BrokerReportLastShareRecord
} from '../../types/BrokerReportGroup';
import {DispatchOrderStatus} from '../../types/DispatchOrderStatus';
import {GlobalContext} from "../admin-frame/AdminFrame";
import {ApiUtility} from "../common/ApiUtility";
import {SelectOption} from "../common/CommonUIElementTypes";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {Constants} from "../common/Constants";
import {DateControl} from "../common/DateControl";
import {DateUtility} from '../common/DateUtility';
import {useRenderableDataStore} from "../common/hooks/useRenderableDataStore";
import {LocationUtility} from "../common/LocationUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {PhoneUtility} from "../common/PhoneUtility";
import {SelectUtility} from "../common/SelectUtility";
import {StringUtility} from "../common/StringUtility";
import {UrlUtility} from "../common/UrlUtility";
import {ActivityDirectionDisplay} from "../dispatch-orders/ActivityDirectionDisplay";

interface BrokerReportsListPageURLQueryParams {
    dateString?: string;
    brokerId?: string;
    statusId?: string;
}

// The shared-status label names the sharing method in one word, while the stored share type name is the fuller label the sharing records panel shows.
let sharedStatusShareTypeWords: {[shareTypeName: string]: string} = {
    [Constants.brokerReportShareTypeNames.linkCreated]: 'Link',
    [Constants.brokerReportShareTypeNames.print]: 'Print',
    [Constants.brokerReportShareTypeNames.sms]: 'SMS',
    [Constants.brokerReportShareTypeNames.email]: 'Email'
};

function sharedStatusText(lastShareRecord?: BrokerReportLastShareRecord | null) {
    if (!lastShareRecord) {
        return 'Not yet sent';
    }

    let shareTypeWord = sharedStatusShareTypeWords[lastShareRecord.share_type_name] || lastShareRecord.share_type_name;
    return `Last shared ${DateUtility.dbDateToSharedStatusDateTime(lastShareRecord.created_at)} · ${shareTypeWord}`;
}

export const BrokerReportsListPage = function () {
    const context = useContext(GlobalContext);
    const urlBase = `/${Constants.routes.brokerReports.pathSegment}`;
    const dataStore = useRenderableDataStore({
        lastRefreshTimestamp: undefined,
        brokerGroups: [] as BrokerReportGroup[],
        loading: true,
        dateString: DateUtility.jsDateToURLDate(new Date()),
        filterBrokerId: null,
        filterStatusId: null,
        brokerOptions: [] as SelectOption[],
        dispatchOrderStatuses: [] as DispatchOrderStatus[],

        prevUrlQueryString: null,
    })
    const location = useLocation();

    // Update state if URL changes.
    if ((location && location.search) !== dataStore.data().prevUrlQueryString) {
        let queryParams: BrokerReportsListPageURLQueryParams = UrlUtility.getQueryParamsFromSearchString(location.search);

        let dateString = dataStore.data().dateString;
        if (queryParams.dateString) {
            dateString = queryParams.dateString;
        }

        let filterBrokerId: any = null;
        if (queryParams.brokerId) {
            filterBrokerId = queryParams.brokerId;
        }

        let filterStatusId: any = null;
        if (queryParams.statusId) {
            filterStatusId = queryParams.statusId;
        }

        dataStore.updateAndRender({
            dateString: dateString,
            filterBrokerId: filterBrokerId,
            filterStatusId: filterStatusId,
            prevUrlQueryString: location.search,
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadListDebounced();
        },
        [
            '' + dataStore.data().dateString,
            '' + dataStore.data().filterBrokerId,
            '' + dataStore.data().filterStatusId
        ]
    );

    const reloadListDebounced = _.debounce(() => {
        let dataStoreUpdate: any = {};
        dataStore.updateAndRender({loading: true})

        ApiUtility.getBrokerReportsListForDate(
            dataStore.data().dateString,
            dataStore.data().filterBrokerId,
            dataStore.data().filterStatusId
        )
            .then(function (listData) {
                dataStoreUpdate = {
                    lastRefreshTimestamp: (new Date()).getTime(),
                    brokerGroups: listData.brokerGroups,
                    loading: false
                }

                // Fetch data for the filter controls one time (if their lists are empty).
                return new Promise<void>(function (resolve, reject) {
                    if (
                        dataStore.data().brokerOptions.length === 0 ||
                        dataStore.data().dispatchOrderStatuses.length === 0
                    ) {
                        return ApiUtility.getBrokersForDropdown()
                            .then(function (brokersList: any[]) {
                                dataStoreUpdate.brokerOptions = SelectUtility.modelArrayToSelectOptions(
                                    brokersList,
                                    'id',
                                    undefined,
                                    (broker: any) => broker.name
                                )

                                return ApiUtility.getDispatchOrderStatuses()
                            })
                            .then(function (statusesList: DispatchOrderStatus[]) {
                                dataStoreUpdate.dispatchOrderStatuses = statusesList

                                resolve()
                            })
                            .catch(reject)

                    } else {
                        resolve()
                    }
                });
            })
            .then(function () {
                dataStore.updateAndRender(dataStoreUpdate);
            })
            .catch(function (error) {
                dataStore.updateAndRender({...dataStoreUpdate, loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the broker reports list page.`);
            });
    }, Constants.debounceTimeoutMs);

    function navigateToUpdatedFilters(updatedQueryParamValues: object) {
        let currentQueryParams: any = UrlUtility.getQueryParamsFromSearchString(location.search);
        let updatedQueryParams: any = {...currentQueryParams, ...updatedQueryParamValues};
        let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(updatedQueryParams);
        context.navigator.navigateTo(urlBase + queryString);
    }

    function handleBrokerChange(option: SelectOption | null) {
        navigateToUpdatedFilters({brokerId: option?.value || null});
    }

    function handleStatusFilterClick(statusId: any) {
        navigateToUpdatedFilters({statusId: statusId || null});
    }

    function handleFilterDateChange(date: Date | null) {
        if (date) {
            let selectedDateString = DateUtility.jsDateToURLDate(date);
            if (dataStore.data().dateString !== selectedDateString) {
                navigateToUpdatedFilters({dateString: selectedDateString});
            }
        }
    }

    function handlePreviousDate() {
        navigateToUpdatedFilters({
            dateString: DateUtility.jsDateToURLDate(
                DateUtility.urlDateToMoment(dataStore.data().dateString).subtract(1, 'days').toDate()
            )
        });
    }

    function handleNextDate() {
        navigateToUpdatedFilters({
            dateString: DateUtility.jsDateToURLDate(
                DateUtility.urlDateToMoment(dataStore.data().dateString).add(1, 'days').toDate()
            )
        });
    }

    function handleViewBrokerReport(brokerId: number) {
        context.navigator.navigateTo(`${urlBase}/${brokerId}/${dataStore.data().dateString}`);
    }

    function renderAssignmentLine(assignmentLine: BrokerReportAssignmentLine, brokerName: string) {
        let assignedMaterial = assignmentLine.assigned_activity_material;
        let roundRobinIcon = undefined;
        if (assignmentLine.is_round_robin) {
            roundRobinIcon = <img
                src="/static/images/round-robin-light-gray-on-textcolor.svg"
                className="broker-report-assignment-line-round-robin-icon"
            />
        }

        return (
            <div className="broker-report-assignment-line" key={`assignment-line-${assignmentLine.id}`}>
                <div className="broker-report-assignment-line-cell broker-report-assignment-line-direction">
                    <ActivityDirectionDisplay
                        directionName={assignmentLine.activity.activity_direction?.name}
                        abbreviated={true}
                    />
                </div>
                <div className="broker-report-assignment-line-cell broker-report-assignment-line-po">
                    <div className="broker-report-assignment-line-cell-text">
                        {assignmentLine.activity.purchase_order_number}
                    </div>
                </div>
                <div className="broker-report-assignment-line-cell broker-report-assignment-line-time">
                    <div className="broker-report-assignment-line-cell-text">
                        {DateUtility.dbDateToDisplayTime(assignmentLine.start_time || assignmentLine.activity.activity_start_time)}
                    </div>
                </div>
                <div className="broker-report-assignment-line-cell broker-report-assignment-line-material">
                    <div className="broker-report-assignment-line-cell-text">
                        {
                            assignedMaterial
                                ? StringUtility.toInitialCaps('' + assignmentLine.assigned_material_amount + ' ' + assignedMaterial.measurement_unit + 's ' + assignedMaterial.name)
                                : ''
                        }
                    </div>
                </div>
                <div className="broker-report-assignment-line-cell broker-report-assignment-line-truck-count">
                    <div className="broker-report-assignment-line-truck-count-text">
                        {assignmentLine.truck_count != null ? String('' + assignmentLine.truck_count).padStart(2, '0') : ''}
                    </div>
                    <div className="broker-report-assignment-line-truck-count-icon-cell">
                        <img
                            className="broker-report-assignment-line-truck-count-icon"
                            src="/static/images/truck-textcolor.svg"
                        />
                    </div>
                    {roundRobinIcon}
                </div>
                <div className="broker-report-assignment-line-cell broker-report-assignment-line-note">
                    <div
                        className="broker-report-assignment-line-cell-text">{assignmentLine.broker_notes}</div>
                </div>
            </div>
        );
    }

    function renderDispatchOrderSection(dispatchOrderSection: BrokerReportDispatchOrderSection, brokerName: string) {
        let statusClassName = '';
        if (dispatchOrderSection.dispatch_order.dispatch_order_status?.name === Constants.dispatchOrderStatusNames.pending) {
            statusClassName = ' in-process';
        } else if (dispatchOrderSection.dispatch_order.dispatch_order_status?.name === Constants.dispatchOrderStatusNames.confirmed) {
            statusClassName = ' confirmed';
        }

        return (
            <div
                className={`broker-report-order-section${statusClassName}`}
                key={`order-section-${dispatchOrderSection.dispatch_order.id}`}
            >
                <div className="broker-report-order-status-dot-cell">
                    <div className="broker-report-order-status-dot"/>
                </div>
                <div className="broker-report-order-assignment-lines">
                    {dispatchOrderSection.activity_broker_assignments.map((assignmentLine) => renderAssignmentLine(assignmentLine, brokerName))}
                </div>
            </div>
        );
    }

    function renderJobBox(jobGroup: BrokerReportJobGroup, brokerName: string) {
        return (
            <div className="broker-report-job-box" key={`job-box-${jobGroup.job.id}`}>
                <div className="broker-report-job-box-info">
                    <div className="broker-report-job-box-info-content">
                        <div className="broker-report-job-number">{jobGroup.job.job_number}</div>
                        <div className="broker-report-job-box-info-right">
                            <div className="broker-report-job-box-info-right-content">
                                <div className="broker-report-job-name">{jobGroup.job.name}</div>
                                <div className="broker-report-job-location">
                                    {jobGroup.job.location ? LocationUtility.locationLabel(jobGroup.job.location) : ''}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="broker-report-job-box-assignments">
                    {jobGroup.dispatchOrderSections.map((dispatchOrderSection) => renderDispatchOrderSection(dispatchOrderSection, brokerName))}
                </div>
            </div>
        );
    }

    function renderForemanGroup(foremanGroup: BrokerReportForemanGroup, brokerName: string) {
        return (
            <div className="broker-report-foreman-group" key={`foreman-group-${foremanGroup.foreman.id}`}>
                <div className="broker-report-foreman-header">
                    <img
                        className="broker-report-foreman-icon"
                        src="/static/images/users-icon-light-gray.svg"
                    />
                    <div className="broker-report-foreman-name-text">{foremanGroup.foreman.person?.name}</div>
                    <div className="broker-report-foreman-phone">
                        {PhoneUtility.dbPhoneToParenthesesDisplayPhone(foremanGroup.foreman.person?.phone)}
                    </div>
                </div>
                <div className="broker-report-job-boxes">
                    {foremanGroup.jobGroups.map((jobGroup) => renderJobBox(jobGroup, brokerName))}
                </div>
            </div>
        );
    }

    function renderBrokerGroup(brokerGroup: BrokerReportGroup) {
        return (
            <div className="broker-report-group" key={`broker-group-${brokerGroup.broker.id}`}>
                <div className="broker-report-group-header">
                    <div className="broker-report-group-name">{brokerGroup.broker.name}</div>
                    <div className="broker-report-group-shared-status">
                        {sharedStatusText(brokerGroup.lastShareRecord)}
                    </div>
                    <div
                        className="broker-report-group-view-report-option"
                        onClick={() => handleViewBrokerReport(brokerGroup.broker.id)}
                    >
                        <div className="broker-report-group-view-report-text">View Broker Report</div>
                        <img
                            className="broker-report-group-view-report-icon"
                            src="/static/images/report-link-light-gray.svg"
                            alt="View Broker Report"
                        />
                    </div>
                </div>
                {brokerGroup.foremanGroups.map((foremanGroup) => renderForemanGroup(foremanGroup, brokerGroup.broker.name))}
            </div>
        );
    }

    function statusIdForStatusName(statusName: string) {
        let matchingStatus = dataStore.data().dispatchOrderStatuses.find(
            (dispatchOrderStatus: DispatchOrderStatus) => dispatchOrderStatus.name === statusName
        );
        return matchingStatus?.id;
    }

    if (dataStore.data().lastRefreshTimestamp) {
        // The design writes the In Process status as "In-Process" on this page's filter button only; the stored status name keeps its space.
        let statusFilterOptions = [
            {label: 'All', statusId: null},
            {label: 'In-Process', statusId: statusIdForStatusName(Constants.dispatchOrderStatusNames.pending)},
            {label: 'Confirmed', statusId: statusIdForStatusName(Constants.dispatchOrderStatusNames.confirmed)},
        ];

        return (
            <div className="common-list-page broker-reports-list-page">
                <div className="common-list-page-filters-header">
                    <div className="common-list-page-filters-header-content">
                        <div className="common-list-page-filters-header-content-left">
                            <DateControl
                                dateString={dataStore.data().dateString}
                                onPreviousDate={handlePreviousDate}
                                onNextDate={handleNextDate}
                                onDateChange={handleFilterDateChange}
                            />
                        </div>
                        <div className="common-list-page-filters-header-content-right">
                            <div className="common-list-page-select-filter-container broker-filter-container">
                                <img
                                    className="common-list-page-filter-icon broker-filter-icon"
                                    src="/static/images/brokers-icon-green.svg"
                                />
                                <Select
                                    className="common-list-page-select-filter"
                                    classNamePrefix="common-list-page-select-filter"
                                    options={dataStore.data().brokerOptions}
                                    value={SelectUtility.selectOptionForId(
                                        '' + dataStore.data().filterBrokerId,
                                        dataStore.data().brokerOptions
                                    )}
                                    placeholder="Broker"
                                    isClearable={true}
                                    onChange={(option) => handleBrokerChange(option as SelectOption | null)}
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 0,
                                        colors: {
                                            ...theme.colors,
                                            text: '#575A4B',
                                            primary25: '#8FCB9B25',
                                            primary50: '#8FCB9B50',
                                            primary: '#8FCB9B',
                                        },
                                    })}
                                    components={{
                                        DropdownIndicator: (dropdownIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-filter-caret-container"
                                                {...(dropdownIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-filter-caret"
                                                    src="/static/images/caret-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                        ClearIndicator: (clearIndicatorProps) => (
                                            <div
                                                className="common-list-page-select-close-container"
                                                {...(clearIndicatorProps.innerProps || {})}
                                            >
                                                <img
                                                    className="common-list-page-select-close"
                                                    src="/static/images/close-textcolor.svg"
                                                />
                                            </div>
                                        ),
                                    }}
                                />
                            </div>
                            {statusFilterOptions.map(function (statusFilterOption) {
                                let selectedClassName =
                                    '' + statusFilterOption.statusId === '' + dataStore.data().filterStatusId ? ' selected' : '';
                                return (
                                    <div
                                        key={statusFilterOption.label}
                                        className={`broker-reports-status-filter${selectedClassName}`}
                                        onClick={() => handleStatusFilterClick(statusFilterOption.statusId)}
                                    >
                                        {statusFilterOption.label}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={dataStore.data().loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (dataStore.data().brokerGroups.length > 0) {
                            return (
                                <div className="broker-reports-list-content">
                                    {dataStore.data().brokerGroups.map(renderBrokerGroup)}
                                </div>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No broker reports found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
